import React, { useState } from "react";
import { Button } from "primereact/button";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { useAppDispatch, useAppSelector } from "@core/store/hook.ts";
import {
  createInterimAgent,
  getAllInterimAgents,
  returnInterimAgent,
} from "@core/actions/interimAgentAction.ts";
import { getAllUsers } from "@core/actions/usersActions.ts";
import {
  interimAgentList,
  loadingCreateInterim,
  loadingListInterim,
  loadingReturnInterim,
} from "@core/slice/InterimAgentSlice.ts";
import { userLists } from "@core/slice/UsersSlice.ts";
import { FetchStatusType } from "@core/modeles/model.ts";
import {
  InterimAgentDTO,
  InterimAgentPostDTO,
  InterimReason,
  interimReasonList,
} from "./interimAgent.model.ts";
import { UserDTO } from "../user.model.ts";
import Message from "@shared/message/Message.tsx";
import CustomDialog from "@shared/customDialog.tsx";
import Spinner from "@shared/Spinner.tsx";


const InterimAgentPage: React.FC = () => {
  const dispatch = useAppDispatch();

  const interimResponse = useAppSelector(interimAgentList);
  const usersResponse = useAppSelector(userLists);
  const loadingList = useAppSelector(loadingListInterim);
  const loadingCreate = useAppSelector(loadingCreateInterim);
  const loadingReturn = useAppSelector(loadingReturnInterim);

  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [open, setOpen] = useState(false);
  const [visibleReturnDialog, setVisibleReturnDialog] = useState(false);
  const [selectedInterim, setSelectedInterim] =
    useState<InterimAgentDTO | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<{ name: string; code: string } | null>(null);
  const [messageInfo, setMessageInfo] = useState<{
    severity: "success" | "info" | "warn" | "error";
    summary: string;
    detail: string;
  }>({ severity: "success", summary: "", detail: "" });

  const [formData, setFormData] = useState<{
    absentAgent: UserDTO | null;
    interimAgent: UserDTO | null;
    reason: { name: string; code: InterimReason } | null;
    startDate: Date | null;
    endDate: Date | null;
    comment: string;
  }>({
    absentAgent: null,
    interimAgent: null,
    reason: null,
    startDate: null,
    endDate: null,
    comment: "",
  });

  const [formErrors, setFormErrors] = useState({
    absentAgent: "",
    interimAgent: "",
    reason: "",
    startDate: "",
    endDate: "",
  });

  const statusOptions = [
    { name: "Actif", code: "true" },
    { name: "Inactif", code: "false" },
  ];

  const fetchList = (page: number, size: number, search: string, status: string | null) => {
    dispatch(
      getAllInterimAgents({
        page,
        size,
        sortBy: "createdAt",
        sortDir: "desc",
        search,
        ...(status !== null ? { active: status } : {}),
      })
    );
  };

  React.useEffect(() => {
    fetchList(1, pageSize, "", null);
    dispatch(
      getAllUsers({
        page: 1,
        size: 1000,
        sortBy: "firstName",
        sortDir: "asc",
        search: "",
        access: "",
        localisation: "",
        profil: "",
      })
    );
  }, [dispatch, pageSize]);

  const handleSearch = () => {
    setCurrentPage(0);
    fetchList(1, pageSize, searchTerm, filterStatus?.code ?? null);
  };

  const handleReset = () => {
    setSearchTerm("");
    setFilterStatus(null);
    setCurrentPage(0);
    fetchList(1, pageSize, "", null);
  };

  const users: UserDTO[] = usersResponse?.data ?? [];

  const showMessage = (
    severity: "success" | "info" | "warn" | "error",
    summary: string,
    detail: string
  ) => {
    setMessageInfo({ severity, summary, detail });
    setOpen(true);
  };

  const validate = () => {
    const errors = {
      absentAgent: "",
      interimAgent: "",
      reason: "",
      startDate: "",
      endDate: "",
    };
    let valid = true;
    if (!formData.absentAgent) {
      errors.absentAgent = "L'agent absent est requis.";
      valid = false;
    }
    if (!formData.interimAgent) {
      errors.interimAgent = "L'agent intérimaire est requis.";
      valid = false;
    }
    if (
      formData.absentAgent &&
      formData.interimAgent &&
      formData.absentAgent.id === formData.interimAgent.id
    ) {
      errors.interimAgent =
        "L'agent intérimaire doit être différent de l'agent absent.";
      valid = false;
    }
    if (!formData.reason) {
      errors.reason = "Le motif est requis.";
      valid = false;
    }
    if (!formData.startDate) {
      errors.startDate = "La date de début est requise.";
      valid = false;
    }
    if (!formData.endDate) {
      errors.endDate = "La date de fin est requise.";
      valid = false;
    }
    if (
      formData.startDate &&
      formData.endDate &&
      formData.endDate <= formData.startDate
    ) {
      errors.endDate = "La date de fin doit être postérieure à la date de début.";
      valid = false;
    }
    setFormErrors(errors);
    return valid;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const payload: InterimAgentPostDTO = {
      absentAgentId: Number(formData.absentAgent!.id),
      interimAgentId: Number(formData.interimAgent!.id),
      reason: formData.reason!.code,
      startDate: formData.startDate!.toISOString().split("T")[0],
      endDate: formData.endDate!.toISOString().split("T")[0],
      comment: formData.comment || undefined,
    };
    dispatch(
      createInterimAgent({
        filter: payload,
        onSuccess: () => {
          setShowForm(false);
          resetForm();
          showMessage(
            "success",
            "Succès",
            "L'agent intérimaire a été assigné avec succès."
          );
          dispatch(
            getAllInterimAgents({
              page: currentPage + 1,
              size: pageSize,
              sortBy: "createdAt",
              sortDir: "desc",
              search: "",
            })
          );
        },
        onError: () => {
          showMessage(
            "error",
            "Erreur",
            "Une erreur s'est produite lors de l'assignation."
          );
        },
      })
    );
  };

  const handleReturn = (rowData: InterimAgentDTO) => {
    setSelectedInterim(rowData);
    setVisibleReturnDialog(true);
  };

  const confirmReturn = () => {
    if (!selectedInterim) return;
    dispatch(
      returnInterimAgent({
        id: selectedInterim.id,
        onSuccess: () => {
          setVisibleReturnDialog(false);
          setSelectedInterim(null);
          showMessage(
            "success",
            "Succès",
            `Les plaintes ont été retransférées à ${selectedInterim.absentAgent.firstName} ${selectedInterim.absentAgent.lastName} avec succès.`
          );
          dispatch(
            getAllInterimAgents({
              page: currentPage + 1,
              size: pageSize,
              sortBy: "createdAt",
              sortDir: "desc",
              search: "",
            })
          );
        },
        onError: () => {
          showMessage("error", "Erreur", "Une erreur s'est produite, veuillez réessayer.");
        },
      })
    );
  };

  const resetForm = () => {
    setFormData({
      absentAgent: null,
      interimAgent: null,
      reason: null,
      startDate: null,
      endDate: null,
      comment: "",
    });
    setFormErrors({
      absentAgent: "",
      interimAgent: "",
      reason: "",
      startDate: "",
      endDate: "",
    });
  };

  const onPageChange = (e: DataTableStateEvent) => {
    setCurrentPage(e.page ?? 0);
    setPageSize(e.rows);
    fetchList((e.page ?? 0) + 1, e.rows, searchTerm, filterStatus?.code ?? null);
  };

  const reasonLabel = (reason: InterimReason) => {
    return interimReasonList.find((r) => r.code === reason)?.name ?? reason;
  };

  const statusBody = (rowData: InterimAgentDTO) => (
    <Tag
      value={rowData.active ? "Actif" : "Inactif"}
      severity={rowData.active ? "success" : "danger"}
    />
  );

  const agentBody = (rowData: InterimAgentDTO, field: "absentAgent" | "interimAgent") => {
    const agent = rowData[field];
    return agent ? `${agent.firstName} ${agent.lastName}` : "-";
  };

  const actionsBody = (rowData: InterimAgentDTO) => (
    <div className="flex gap-1">
      <Button
        icon="pi pi-user-edit"
        className="p-button-rounded p-button-success p-button-text"
        tooltip="Retour de l'agent"
        tooltipOptions={{ position: "top" }}
        disabled={!rowData.active}
        onClick={() => handleReturn(rowData)}
      />
    </div>
  );

  const formatDate = (date: string) =>
    date ? new Date(date).toLocaleDateString("fr-FR") : "-";

  const formDialogFooter = (
    <div className="flex gap-3 justify-center">
      <Button
        label="Annuler"
        icon="pi pi-times"
        className="px-6 py-2 rounded-md bg-[#6C757D] border-none text-white font-medium"
        onClick={() => {
          setShowForm(false);
          resetForm();
        }}
        disabled={loadingCreate === FetchStatusType.LOADING}
      />
      <Button
        label="Enregistrer"
        icon="pi pi-check"
        className="px-6 py-2 rounded-md bg-[#086D33] border-none text-white font-medium"
        onClick={handleSubmit}
        loading={loadingCreate === FetchStatusType.LOADING}
      />
    </div>
  );

  return (
    <div className="p-6 bg-white rounded-lg min-h-screen w-full">
      <Message
        open={open}
        severity={messageInfo.severity}
        summary={messageInfo.summary}
        detail={messageInfo.detail}
      />

      <CustomDialog
        visible={visibleReturnDialog}
        title={`L'agent ${selectedInterim?.absentAgent.firstName ?? ""} ${selectedInterim?.absentAgent.lastName ?? ""} est de retour. Souhaitez-vous lui retransférer toutes ses plaintes ?`}
        rejectLabel="Non"
        confirmLabel="Oui, retransférer"
        confirmDialog={confirmReturn}
        rejectDialog={() => setVisibleReturnDialog(false)}
        loading={loadingReturn === FetchStatusType.LOADING}
        disabled={loadingReturn === FetchStatusType.LOADING}
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">
            Gestion des agents intérimaires
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Transférez les plaintes d'un agent absent (congé, maladie, mission)
            vers un agent intérimaire.
          </p>
        </div>
        <Button
          label="Assigner un intérimaire"
          icon="pi pi-user-plus"
          className="px-6 py-2 rounded-md bg-[#086D33] border-none text-white font-medium"
          onClick={() => setShowForm(true)}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">Recherche (agent)</label>
          <InputText
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Nom de l'agent..."
            className="w-56"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">Statut</label>
          <Dropdown
            value={filterStatus}
            options={statusOptions}
            optionLabel="name"
            onChange={(e) => setFilterStatus(e.value)}
            placeholder="Tous"
            className="w-40"
            showClear
          />
        </div>
        <Button
          label="Rechercher"
          icon="pi pi-search"
          className="px-5 py-2 rounded-md bg-[#086D33] border-none text-white font-medium"
          onClick={handleSearch}
        />
        <Button
          label="Réinitialiser"
          icon="pi pi-refresh"
          className="px-5 py-2 rounded-md bg-[#6C757D] border-none text-white font-medium"
          onClick={handleReset}
        />
      </div>

      {/* Table */}
      {loadingList === FetchStatusType.LOADING ? (
        <div className="flex justify-center items-center min-h-[300px]">
          <Spinner />
        </div>
      ) : (
        <DataTable
          value={interimResponse?.data ?? []}
          paginator
          lazy
          rows={pageSize}
          totalRecords={interimResponse?.total ?? 0}
          first={currentPage * pageSize}
          onPage={onPageChange}
          rowsPerPageOptions={[10, 25, 50]}
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate="Afficher {first} à {last} sur {totalRecords} lignes"
          paginatorClassName="flex justify-end"
          emptyMessage="Aucun agent intérimaire trouvé."
          className="mt-4"
          stripedRows
        >
          <Column
            header="Agent absent"
            body={(row) => agentBody(row, "absentAgent")}
          />
          <Column
            header="Agent intérimaire"
            body={(row) => agentBody(row, "interimAgent")}
          />
          <Column
            header="Motif"
            body={(row: InterimAgentDTO) => reasonLabel(row.reason)}
          />
          <Column
            field="startDate"
            header="Date début"
            body={(row: InterimAgentDTO) => formatDate(row.startDate)}
          />
          <Column
            field="endDate"
            header="Date fin"
            body={(row: InterimAgentDTO) => formatDate(row.endDate)}
          />
          <Column
            header="Commentaire"
            body={(row: InterimAgentDTO) => row.comment ?? "-"}
          />
          <Column header="Statut" body={statusBody} />
          <Column header="Actions" body={actionsBody} />
        </DataTable>
      )}

      {/* Form Dialog */}
      <Dialog
        header="Assignation d'un agent intérimaire"
        visible={showForm}
        onHide={() => {
          setShowForm(false);
          resetForm();
        }}
        style={{ width: "550px" }}
        footer={formDialogFooter}
        draggable={false}
      >
        <div className="flex flex-col gap-4 mt-2">
          {/* Agent absent */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Agent absent <span className="text-red-500">*</span>
            </label>
<Dropdown
              value={formData.absentAgent}
              options={users.map((u) => ({
                label: `${u.firstName} ${u.lastName}`,
                value: u,
              }))}
              optionLabel="label"
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, absentAgent: e.value.value ?? e.value }))
              }
              placeholder="Sélectionner l'agent absent"
              className={`w-full ${formErrors.absentAgent ? "p-invalid" : ""}`}
              filter
              filterBy="label"
            />
            {formErrors.absentAgent && (
              <small className="text-red-500">{formErrors.absentAgent}</small>
            )}
          </div>

          {/* Agent intérimaire */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Agent intérimaire <span className="text-red-500">*</span>
            </label>
<Dropdown
              value={formData.interimAgent}
              options={users.map((u) => ({
                label: `${u.firstName} ${u.lastName}`,
                value: u,
              }))}
              optionLabel="label"
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, interimAgent: e.value.value ?? e.value }))
              }
              placeholder="Sélectionner l'agent intérimaire"
              className={`w-full ${formErrors.interimAgent ? "p-invalid" : ""}`}
              filter
              filterBy="label"
            />
            {formErrors.interimAgent && (
              <small className="text-red-500">{formErrors.interimAgent}</small>
            )}
          </div>

          {/* Motif */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Motif d'absence <span className="text-red-500">*</span>
            </label>
            <Dropdown
              value={formData.reason}
              options={interimReasonList}
              optionLabel="name"
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, reason: e.value }))
              }
              placeholder="Sélectionner le motif"
              className={`w-full ${formErrors.reason ? "p-invalid" : ""}`}
            />
            {formErrors.reason && (
              <small className="text-red-500">{formErrors.reason}</small>
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Date de début <span className="text-red-500">*</span>
              </label>
              <Calendar
                value={formData.startDate}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    startDate: e.value as Date | null,
                  }))
                }
                dateFormat="dd/mm/yy"
                showIcon
                className={`w-full ${formErrors.startDate ? "p-invalid" : ""}`}
                placeholder="JJ/MM/AAAA"
              />
              {formErrors.startDate && (
                <small className="text-red-500">{formErrors.startDate}</small>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Date de fin <span className="text-red-500">*</span>
              </label>
              <Calendar
                value={formData.endDate}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    endDate: e.value as Date | null,
                  }))
                }
                dateFormat="dd/mm/yy"
                showIcon
                className={`w-full ${formErrors.endDate ? "p-invalid" : ""}`}
                placeholder="JJ/MM/AAAA"
                minDate={formData.startDate ?? undefined}
              />
              {formErrors.endDate && (
                <small className="text-red-500">{formErrors.endDate}</small>
              )}
            </div>
          </div>

          {/* Commentaire */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Commentaire (optionnel)
            </label>
            <InputText
              value={formData.comment}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, comment: e.target.value }))
              }
              placeholder="Ajouter un commentaire..."
              className="w-full"
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default InterimAgentPage;
